import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { CircularProgress } from "@mui/material";

import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "95%",
  maxWidth: 450,
  bgcolor: "background.paper",
  border: "2px solid #000",
  borderRadius: "10px",
  boxShadow: 24,
  paddingTop: 1,
  paddingBottom: 4,
  paddingLeft: 4,
  paddingRight: 4,
};

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiTextField-root": {
      margin: theme.spacing(1),
      width: "32ch",
    },
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  paper: {
    position: "absolute",
    width: 300,
    backgroundColor: "#fff",
    boxShadow: "0 0 10px rgba(0, 0, 0, 0.5)",
    padding: 20,
    outline: "none",
    borderRadius: 5,
    overflow: "hidden",
  },
  label: {
    fontWeight: "bold",
  },
}));

const INITIAL_FORM_STATE = {
  name: "",
};

// Tenant only supports editing. There is no create mode and no delete —
// this component is always opened against an existing tenant record.
export function MyTenantForm({ tenantData, setSelectedTenantForEdit, onSuccess }) {
  const [open, setOpen] = useState(false);

  const classes = useStyles();
  const headers = useSelector((state) => state.header);
  const api = localStorage.getItem("api");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [error, setError] = useState(false);

  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [initialTenantData, setInitialTenantData] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const editMode = !!tenantData?.id;

  useEffect(() => {
    if (tenantData?.id) {
      const initialData = {
        name: tenantData?.name ?? "",
        domain:tenantData?.domain ?? ""
      };

      setInitialTenantData(initialData);
      setFormData(initialData);
      setOpen(true);
    }
  }, [tenantData]);

  const handleClose = () => {
    setOpen(false);
    setSelectedTenantForEdit?.({});
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const trimmedName = formData.name.trim();

    if (trimmedName.length === 0) {
      setErrorModalOpen(true);
      setError(true);
      setErrorMessage("Please provide a name");
      return false;
    }

    return true;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!editMode) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    const trimmedName = formData.name.trim();
    const trimmedDomain =formData?.domain?.trim()

    if (trimmedName === initialTenantData.name && trimmedDomain  ===initialTenantData?.domain) {
      setErrorModalOpen(true);
      setError(true);
      setErrorMessage("No changes to save");
      return;
    }

    setIsSubmitting(true);

    const body = {
        id: tenantData.id,
      };

      if (trimmedName !== initialTenantData.name) {
        body.name = trimmedName;
      }

      if (trimmedDomain !== initialTenantData.domain) {
        body.domain = trimmedDomain;
      }

      const requestBody = {
        path: "tenant",
        query: {},
        body,
        requestType: "POST",
      };

    axios
      .post(api + `/auth/piggyback`, requestBody, headers)
      .then((response) => {
        setIsSubmitting(false);
        setErrorModalOpen(true);
        setError(false);
        setErrorMessage("Tenant updated successfully");
        onSuccess?.(response.data);
      })
      .catch((error) => {
        setIsSubmitting(false);
        setErrorModalOpen(true);
        setError(true);
        setErrorMessage(`Error updating tenant: ${error.response?.data}`);
      });
  };

  const handleModalClose = () => {
    setErrorModalOpen(false);
    setErrorMessage("");

    if (!error) {
      setOpen(false);
      setSelectedTenantForEdit?.({});
    }
  };

  const getLabelWithAsterisk = (labelText, isRequired) => {
    return (
      <div style={{ display: "flex", alignItems: "center", width: "100px" }}>
        <label className={classes.label} htmlFor={labelText}>
          {labelText}
        </label>
        {isRequired && <span style={{ color: "red" }}>*</span>}
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <form
          className={classes.root}
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit}
        >
          <div>
            <div>
              <h2>Edit Tenant</h2>
              <h4 style={{ fontWeight: "100" }}>
                Update the name for {tenantData?.domain ?? "this tenant"}
              </h4>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "8px",
              }}
            >
              <div style={{ minWidth: "110px" }}>
                {getLabelWithAsterisk("Name:", true)}
              </div>
              <TextField
                required
                id="name"
                name="name"
                label="Tenant Name"
                size="small"
                variant="outlined"
                value={formData.name}
                onChange={handleChange}
                sx={{ width: isMobile ? "100%" : "350px" }}
              />
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "8px",
              }}
            >
              <div style={{ minWidth: "110px" }}>
                {getLabelWithAsterisk("Domain:", true)}
              </div>
              <TextField
                id="domain"
                name="domain"
                label="Domain"
                size="small"
                variant="outlined"
                value={formData?.domain ?? ""}
                onChange={handleChange}
                sx={{ width: isMobile ? "100%" : "350px" }}
              />
            </div>
          </div>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "end",
              marginRight: "2%",
            }}
          >
            <Button
              style={{ textTransform: "none" }}
              variant="outlined"
              color="error"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              style={{ textTransform: "none" }}
              type="submit"
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} /> : "Save"}
            </Button>
          </Box>

          <Modal
            open={errorModalOpen}
            onClose={handleModalClose}
            className={classes.modal}
          >
            <div className={classes.paper}>
              <h2>{error === true ? "Oops" : "Congratulations"}</h2>
              <p>{errorMessage}</p>
              <Button
                style={{ textTransform: "none", marginLeft: "75%" }}
                onClick={handleModalClose}
                variant="contained"
                color="primary"
              >
                Close
              </Button>
            </div>
          </Modal>
        </form>
      </Box>
    </Modal>
  );
}